import React from "react";

import { Localized } from "@fluent/react";
import cc from "classcat";
import { Row } from "@narmi/design_system";
import { format, parseISO } from "date-fns";
import { useAccount } from "byzantine/src/dbbl/businessLogic/entities";

import styles from "./DocumentsList.module.scss";

import type { WebDocument } from "byzantine/src/dbbl/businessLogic/entities/documents/types";

const formatDocumentDate = (createdAt: string) => {
  const dateOnlyValue = createdAt.split("T")[0];

  return format(parseISO(dateOnlyValue), "MMM d, yyyy");
};

const DocumentsListRow = ({
  document,
  viewUrl,
}: {
  document: WebDocument;
  viewUrl?: string;
}) => {
  const account = useAccount(document.account_id);
  return (
    <React.Fragment>
      <a
        className={styles["documents-page-list-document"]}
        key={document.id}
        href={viewUrl}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`document-list-item-${document.id}`}
      >
        <span
          className={cc([
            "narmi-icon-file-text1",
            styles["documents-page-list-document-icon"],
          ])}
        />
        <div className={styles["documents-page-list-document-info"]}>
          <Row justifyContent="space-between">
            <span
              className={styles["documents-page-list-document-info-name"]}
              data-testid={`document-list-type-${document.id}`}
            >
              {document.type_label}
            </span>
            <div
              className={styles["documents-page-list-document-date"]}
              data-testid={`document-list-date-${document.id}`}
            >
              {formatDocumentDate(document.created_at)}
            </div>
          </Row>
          <span
            className={styles["documents-page-list-document-info-description"]}
            data-testid={`document-list-description-${document.id}`}
          >
            {account?.getShortDescription(false) || document.description}
          </span>
        </div>
      </a>
      <div className={styles["documents-page-list-document-divider"]} />
    </React.Fragment>
  );
};

const DocumentsList = ({
  documents,
  isHtmlJson,
  emptyMessageId = "empty-filter-documents",
}: {
  documents: WebDocument[];
  isHtmlJson: boolean;
  // Fluent ID of the message shown when there are no documents.
  emptyMessageId?: string;
}) => {
  return (
    <div>
      {documents.length === 0 ? (
        <span data-testid="documents-list-empty-message">
          <Localized id={emptyMessageId} />
        </span>
      ) : null}
      {documents.map((document) => {
        const viewUrl = isHtmlJson
          ? document.www_url
          : `${document.www_download_url}?download=0`;
        return (
          <DocumentsListRow
            document={document}
            viewUrl={viewUrl}
            key={document.id}
          />
        );
      })}
    </div>
  );
};

export default DocumentsList;
